import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { X, Mail, Phone, Share2, Facebook, Instagram, Twitter, Linkedin, Youtube } from 'lucide-react';

const ContactsModal = ({ isOpen, onClose, contacts }) => {
  if (!isOpen) return null;

  // Handle cases where contacts might be null, undefined, or malformed
  if (!contacts) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={onClose} />
        <Card className="relative w-full max-w-md mx-4 shadow-lg max-h-[600px] flex flex-col">
          <CardHeader className="pb-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Phone className="w-5 h-5 text-primary" />
                Contacts
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 min-w-0">
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">No contact information available</p>
            </div>
            <div className="flex justify-end pt-4">
              <Button onClick={onClose} variant="outline">Close</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { emails = [], phones = [], socials = {} } = contacts;

  // Debug logging
  console.log('ContactsModal received contacts:', contacts);
  console.log('Emails:', emails);
  console.log('Phones:', phones);
  console.log('Socials:', socials);

  // Get social media icon
  const getSocialIcon = (platform) => {
    const platformLower = platform.toLowerCase();
    if (platformLower.includes('facebook')) return <Facebook className="h-4 w-4" />;
    if (platformLower.includes('instagram')) return <Instagram className="h-4 w-4" />;
    if (platformLower.includes('twitter') || platformLower.includes('x.com')) return <Twitter className="h-4 w-4" />;
    if (platformLower.includes('linkedin')) return <Linkedin className="h-4 w-4" />;
    if (platformLower.includes('youtube')) return <Youtube className="h-4 w-4" />;
    return <Share2 className="h-4 w-4" />;
  };

  // Format platform name for display
  const formatPlatformName = (platform) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  // Convert socials object to array
  const socialsList = Object.entries(socials || {});

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <Card className="relative w-full max-w-md mx-4 shadow-lg max-h-[600px] flex flex-col">
        <CardHeader className="pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Phone className="w-5 h-5 text-primary" />
              Contacts
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 overflow-y-auto flex-1 min-w-0">
          {/* Emails Section */}
          {emails.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email Addresses
              </h3>
              <div className="space-y-1">
                {emails.map((email, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded-md">
                    <span className="text-sm font-mono break-all flex-1 mr-2">{email}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = `mailto:${email}`;
                        a.target = '_blank';
                        a.rel = 'noopener noreferrer';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }}
                      className="h-6 px-2 text-xs flex-shrink-0 hover:bg-blue-50 hover:text-blue-600 hover:scale-105 transition-all duration-200"
                    >
                      Send
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Phones Section */}
          {phones.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Phone Numbers
              </h3>
              <div className="space-y-1">
                {phones.map((phone, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded-md">
                    <span className="text-sm font-mono break-all flex-1 mr-2">{phone}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = `tel:${phone}`;
                        a.target = '_blank';
                        a.rel = 'noopener noreferrer';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }}
                      className="h-6 px-2 text-xs flex-shrink-0 hover:bg-green-50 hover:text-green-600 hover:scale-105 transition-all duration-200"
                    >
                      Call
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Socials Section */}
          {socialsList.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Share2 className="h-4 w-4" />
                Social Media
              </h3>
              <div className="space-y-1">
                {socialsList.map(([platform, url], index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded-md">
                    <div className="flex items-center gap-2 flex-1 mr-2">
                      {getSocialIcon(platform)}
                      <span className="text-sm break-all">{formatPlatformName(platform)}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(url, '_blank')}
                      className="h-6 px-2 text-xs flex-shrink-0 hover:bg-purple-50 hover:text-purple-600 hover:scale-105 transition-all duration-200"
                    >
                      Visit
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No contacts message */}
          {emails.length === 0 && phones.length === 0 && socialsList.length === 0 && (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">No contact information available</p>
            </div>
          )}

          {/* Close button */}
          <div className="flex justify-end pt-4 flex-shrink-0">
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactsModal;
